import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { toast } from "sonner";
import { PageHeader } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ChevronLeft, ChevronRight, Plus, Check, Trash2, CalendarDays } from "lucide-react";

const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
const MONTHS = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
const DOW = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const STATUS_STYLE = {
  planned: "bg-slate-100 text-slate-700 border-slate-200",
  in_progress: "bg-blue-100 text-blue-700 border-blue-200",
  done: "bg-emerald-100 text-emerald-700 border-emerald-200",
};
const PRIORITY_BAR = { High: "border-l-red-500", Medium: "border-l-amber-500", Low: "border-l-slate-300" };
const STATUS_LABEL = { planned: "Planned", in_progress: "In progress", done: "Done" };
const emptyForm = { title: "", project_id: "none", assignee_id: "none", start_date: "", end_date: "", priority: "Medium", status: "planned", notes: "" };

export default function WorkProgram() {
  const qc = useQueryClient();
  const today = iso(new Date());
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [projectFilter, setProjectFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const monthStart = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
  const gridStart = new Date(monthStart); gridStart.setDate(1 - monthStart.getDay());
  const days = [...Array(42)].map((_, i) => { const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); return d; });
  const rangeFrom = iso(days[0]); const rangeTo = iso(days[41]);

  const { data: items = [] } = useQuery({ queryKey: ["work-program", rangeFrom, rangeTo], queryFn: () => api.get(`/work-program?date_from=${rangeFrom}&date_to=${rangeTo}`).then((r) => r.data) });
  const { data: projects = [] } = useQuery({ queryKey: ["projects-lite"], queryFn: () => api.get("/projects").then((r) => r.data) });
  const { data: employees = [] } = useQuery({ queryKey: ["employees"], queryFn: () => api.get("/employees").then((r) => r.data) });
  const projMap = Object.fromEntries(projects.map((p) => [p.id, p.name]));
  const empMap = Object.fromEntries(employees.map((e) => [e.id, e.name]));

  const filtered = items.filter((it) => (projectFilter === "all" || it.project_id === projectFilter) && (statusFilter === "all" || it.status === statusFilter));
  const forDay = (ds) => filtered.filter((it) => it.start_date <= ds && (it.end_date || it.start_date) >= ds);
  const isOverdue = (it) => it.status !== "done" && (it.end_date || it.start_date) < today;

  const counts = { planned: 0, in_progress: 0, done: 0, overdue: 0 };
  filtered.forEach((it) => { counts[it.status] = (counts[it.status] || 0) + 1; if (isOverdue(it)) counts.overdue += 1; });

  const openAdd = (ds) => { setEditingId(null); setForm({ ...emptyForm, start_date: ds, end_date: ds }); setOpen(true); };
  const openEdit = (it) => {
    setEditingId(it.id);
    setForm({ title: it.title, project_id: it.project_id || "none", assignee_id: it.assignee_id || "none", start_date: it.start_date, end_date: it.end_date || it.start_date, priority: it.priority || "Medium", status: it.status || "planned", notes: it.notes || "" });
    setOpen(true);
  };
  const refresh = () => qc.invalidateQueries({ queryKey: ["work-program"] });
  const save = async () => {
    if (!form.title.trim()) { toast.error("Enter an activity title."); return; }
    if (!form.start_date) { toast.error("Pick a start date."); return; }
    const payload = { title: form.title.trim(), project_id: form.project_id === "none" ? null : form.project_id, assignee_id: form.assignee_id === "none" ? null : form.assignee_id, start_date: form.start_date, end_date: form.end_date || form.start_date, priority: form.priority, status: form.status, notes: form.notes };
    try {
      if (editingId) await api.patch(`/work-program/${editingId}`, payload);
      else await api.post("/work-program", payload);
      refresh(); setOpen(false); toast.success(editingId ? "Activity updated" : "Activity scheduled");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save activity"); }
  };
  const markDone = async () => { try { await api.patch(`/work-program/${editingId}`, { status: "done" }); refresh(); setOpen(false); toast.success("Marked as done"); } catch { toast.error("Could not update"); } };
  const remove = async () => { try { await api.delete(`/work-program/${editingId}`); refresh(); setOpen(false); toast.success("Activity removed"); } catch { toast.error("Could not delete"); } };

  const Chip = ({ it }) => (
    <button data-testid={`wp-chip-${it.id}`} onClick={(e) => { e.stopPropagation(); openEdit(it); }}
      className={`w-full text-left truncate text-xs px-1.5 py-0.5 mb-1 rounded border border-l-4 ${STATUS_STYLE[it.status]} ${PRIORITY_BAR[it.priority] || ""} ${isOverdue(it) ? "ring-1 ring-red-400" : ""}`}>
      <span className={it.status === "done" ? "line-through opacity-70" : ""}>{it.title}</span>
    </button>
  );

  return (
    <div>
      <PageHeader title="Work Program" subtitle="Plan and track scheduled activities">
        <Button onClick={() => openAdd(today)} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]" data-testid="wp-add-btn"><Plus className="h-4 w-4 mr-2" /> Schedule activity</Button>
      </PageHeader>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="wp-summary">
        {[["Planned", counts.planned, "text-slate-700"], ["In progress", counts.in_progress, "text-blue-600"], ["Done", counts.done, "text-emerald-600"], ["Overdue", counts.overdue, "text-red-600"]].map(([l, n, c]) => (
          <div key={l} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="text-xs uppercase tracking-wide text-slate-400">{l}</div>
            <div className={`text-2xl font-bold font-mono-num mt-1 ${c}`}>{n}</div>
          </div>
        ))}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" data-testid="wp-prev" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))}><ChevronLeft className="h-4 w-4" /></Button>
          <div className="font-heading font-semibold text-slate-800 text-lg w-44 text-center" data-testid="wp-month">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</div>
          <Button variant="outline" size="icon" data-testid="wp-next" onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))}><ChevronRight className="h-4 w-4" /></Button>
          <Button variant="outline" size="sm" data-testid="wp-today" onClick={() => { const d = new Date(); setCursor(new Date(d.getFullYear(), d.getMonth(), 1)); }}>Today</Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={projectFilter} onValueChange={setProjectFilter}>
            <SelectTrigger className="w-[200px]" data-testid="wp-project-filter"><SelectValue placeholder="All projects" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All projects</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[150px]" data-testid="wp-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger>
            <SelectContent><SelectItem value="all">All statuses</SelectItem><SelectItem value="planned">Planned</SelectItem><SelectItem value="in_progress">In progress</SelectItem><SelectItem value="done">Done</SelectItem></SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="wp-calendar">
        <div className="grid grid-cols-7 border-b border-slate-200 bg-slate-50">
          {DOW.map((d) => <div key={d} className="px-2 py-2 text-xs font-semibold text-slate-500 text-center">{d}</div>)}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d, i) => {
            const ds = iso(d);
            const inMonth = d.getMonth() === cursor.getMonth();
            const dayItems = forDay(ds);
            return (
              <div key={i} data-testid={`wp-day-${ds}`} onClick={() => openAdd(ds)}
                className={`min-h-[104px] border-b border-r border-slate-100 p-1.5 cursor-pointer hover:bg-slate-50/60 transition-colors ${inMonth ? "" : "bg-slate-50/40"}`}>
                <div className={`text-xs font-semibold mb-1 ${ds === today ? "text-white bg-[#1E3A8A] rounded-full w-5 h-5 flex items-center justify-center" : inMonth ? "text-slate-600" : "text-slate-300"}`}>{d.getDate()}</div>
                {dayItems.slice(0, 4).map((it) => <Chip key={it.id} it={it} />)}
                {dayItems.length > 4 && <div className="text-[10px] text-slate-400 px-1">+{dayItems.length - 4} more</div>}
              </div>
            );
          })}
        </div>
      </div>

      <Dialog open={open} onOpenChange={(v) => { if (!v) setOpen(false); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle className="flex items-center gap-2"><CalendarDays className="h-5 w-5" /> {editingId ? "Edit activity" : "Schedule activity"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-xs text-slate-500">Activity</label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="What needs to be done?" data-testid="wp-form-title" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">Start date</label>
                <Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} data-testid="wp-form-start" /></div>
              <div><label className="text-xs text-slate-500">End date</label>
                <Input type="date" value={form.end_date} min={form.start_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} data-testid="wp-form-end" /></div>
            </div>
            <div><label className="text-xs text-slate-500">Project (optional)</label>
              <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
                <SelectTrigger data-testid="wp-form-project"><SelectValue placeholder="No project" /></SelectTrigger>
                <SelectContent><SelectItem value="none">No project</SelectItem>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-xs text-slate-500">Assignee (optional)</label>
                <Select value={form.assignee_id} onValueChange={(v) => setForm({ ...form, assignee_id: v })}>
                  <SelectTrigger data-testid="wp-form-assignee"><SelectValue placeholder="Unassigned" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Unassigned</SelectItem>{employees.map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><label className="text-xs text-slate-500">Priority</label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger data-testid="wp-form-priority"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="High">High</SelectItem><SelectItem value="Medium">Medium</SelectItem><SelectItem value="Low">Low</SelectItem></SelectContent>
                </Select></div>
            </div>
            <div><label className="text-xs text-slate-500">Status</label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="wp-form-status"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="planned">Planned</SelectItem><SelectItem value="in_progress">In progress</SelectItem><SelectItem value="done">Done</SelectItem></SelectContent>
              </Select></div>
            <div><label className="text-xs text-slate-500">Notes (optional)</label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} data-testid="wp-form-notes"
                className="w-full px-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A8A]/30 resize-y" /></div>
          </div>
          <DialogFooter className="flex items-center justify-between gap-2 sm:justify-between">
            <div>
              {editingId && <Button variant="ghost" onClick={remove} data-testid="wp-delete" className="text-red-600 hover:text-red-700 hover:bg-red-50"><Trash2 className="h-4 w-4 mr-1" /> Delete</Button>}
            </div>
            <div className="flex items-center gap-2">
              {editingId && form.status !== "done" && <Button variant="outline" onClick={markDone} data-testid="wp-mark-done"><Check className="h-4 w-4 mr-1" /> Mark done</Button>}
              <Button onClick={save} className="bg-[#1E3A8A] hover:bg-[#1D4ED8]" data-testid="wp-save">{editingId ? "Save" : "Schedule"}</Button>
            </div>
          </DialogFooter>
          {(form.project_id !== "none" || form.assignee_id !== "none") && (
            <div className="text-xs text-slate-400 -mt-2">{form.project_id !== "none" ? `Project: ${projMap[form.project_id] || "—"}` : ""}{form.project_id !== "none" && form.assignee_id !== "none" ? " · " : ""}{form.assignee_id !== "none" ? `Assignee: ${empMap[form.assignee_id] || "—"}` : ""}</div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
